import { useEffect, useMemo, useRef, useState } from 'react';

import type { Reservation } from '../../pages/cardex/api/useReservation';
import type { WhatsappTemplate } from '../../pages/settings/whatsapp/useWhatsappTemplate';

import BottomSheet from './BottomSheet';
import EtatErreur from './EtatErreur';
import Text from './Text';
import EtapeChoixReservation from './feuilleModeles/EtapeChoixReservation';
import EtapeVerificationModele from './feuilleModeles/EtapeVerificationModele';
import { apercu, nomLisible } from './feuilleModeles/helpers';
import { languageName } from '../../helpers/languageName';
import { langueMobile, t } from '../i18n/langue';

/**
 * La feuille de modèles WhatsApp — #19 §4 et §7.
 *
 * **Le nom en titre, le contenu en dessous — retour client 2026-09-25.** #19
 * n'affichait que le CONTENU (`apercu()`) : le web affiche `template_name` dans
 * son `<Select>` (`WhatsappContent.tsx:73`), un identifiant Meta en `snake_case`
 * illisible, rattrapé sur ordinateur par l'aperçu affiché à côté. Le retour
 * client a montré que le NOM (`name`, donné dans TriggerFlow) aide justement à
 * identifier le message, une fois qu'il est lisible : chaque ligne porte donc ce
 * nom en titre, et les premiers mots du contenu en dessous, tronqués à deux
 * lignes. Un nom vide, recopié de `template_name`, ou lui-même en `snake_case`
 * est humanisé plutôt qu'affiché brut — jamais un identifiant technique à l'écran
 * (voir `nomLisible()` dans `feuilleModeles/helpers.ts`).
 *
 * **Pas d'onglets de langue** : celle de la cliente est affichée, pas choisie. Et
 * `{{firstname}}` reste brut dans l'aperçu au lot 1, comme sur le web — les 89
 * paramètres en base sont tous des variables système résolues côté serveur, il n'y
 * a **rien à remplir**.
 *
 * **Ne sont proposés que les modèles qui aboutissent** — c'est le critère « aucun
 * modèle proposé ne peut échouer en 131047 » :
 *
 * - **30 des 65 modèles approuvés (46 %)** exigent une réservation. Ils disparaissent
 *   pour les **187 clientes sur 1 068 (18 %)** qui n'en ont aucune.
 * - **Une seule réservation → retenue d'office, aucun geste** : **755 sur 1 068 (71 %)**.
 * - Plusieurs → le choix s'ouvre **au moment de choisir le modèle**, pas au moment
 *   d'envoyer.
 *
 * Le web fait l'inverse : aucune présélection même à une seule réservation, et à
 * zéro réservation la modale s'ouvre quand même pour annoncer qu'il n'y en a pas —
 * après le choix du modèle et le tap sur Envoyer. Le mobile est délibérément plus
 * juste ici : ce n'est pas une règle métier, rien ne diverge en sécurité.
 *
 * **On relit avant d'envoyer** (audit 2026-09-23) : le tap ouvre une vérification
 * du texte complet ; seul « Envoyer ce message » envoie. Rien n'est désactivé
 * (ADR 0005).
 */
interface FeuilleModelesProps {
    ouverte: boolean;
    onClose: () => void;
    modeles: WhatsappTemplate[];
    reservations: Reservation[];
    /** La locale de la cliente, affichée et non choisie. */
    locale?: string | null;
    onChoisir: (modele: WhatsappTemplate, reservationId?: number) => void;
    /**
     * `modeles` charge encore — audit 2026-09-23. Sans ce témoin, une liste vide
     * en cours de chargement se lisait « aucun modèle », alors qu'on ne le sait
     * pas encore.
     */
    chargement?: boolean;
    /** Échec de chargement sans aucune donnée : dit tel quel, avec « Réessayer ». */
    erreur?: boolean;
    onReessayer?: () => void;
}

export default function FeuilleModeles({
    ouverte,
    onClose,
    modeles,
    reservations,
    locale,
    onChoisir,
    chargement = false,
    erreur = false,
    onReessayer = () => undefined,
}: FeuilleModelesProps) {
    // Quand plusieurs réservations existent, le modèle choisi attend la sienne.
    const [enAttenteDeResa, setEnAttenteDeResa] = useState<WhatsappTemplate | null>(null);
    // Audit 2026-09-23 : un modèle part payant et sans retour. On le relit EN ENTIER
    // avant de l'envoyer ; l'aperçu de la liste est tronqué à deux lignes.
    const [aConfirmer, setAConfirmer] = useState<{ modele: WhatsappTemplate; reservationId?: number } | null>(null);

    const proposables = useMemo(
        () => modeles.filter((m) => !m.requires_reservation || reservations.length > 0),
        [modeles, reservations.length],
    );

    /*
     * Changer d'étape retire le bouton qui avait le focus : il tombait sur `body`,
     * et VoiceOver perdait le fil. Le titre de la nouvelle étape le reprend
     * (relecture M4). Seulement aux étapes réservation et vérification : la liste
     * est l'ouverture de la feuille, dont le focus appartient à `BottomSheet`.
     */
    const etape = aConfirmer ? 'verification' : enAttenteDeResa ? 'reservation' : 'liste';
    const refTitreEtape = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        if (etape !== 'liste') refTitreEtape.current?.focus();
    }, [etape]);

    const choisir = (modele: WhatsappTemplate) => {
        if (!modele.requires_reservation) return setAConfirmer({ modele });

        // 71 % des cas : une seule réservation, retenue d'office.
        if (reservations.length === 1) return setAConfirmer({ modele, reservationId: reservations[0].id });

        setEnAttenteDeResa(modele);
    };

    const fermer = () => {
        setEnAttenteDeResa(null);
        setAConfirmer(null);
        onClose();
    };

    return (
        <BottomSheet
            ouverte={ouverte}
            onClose={fermer}
            libelle={
                aConfirmer
                    ? t('composeur.modeles.verifier')
                    : enAttenteDeResa
                      ? t('composeur.modeles.choisirReservation')
                      : t('composeur.modeles.choisir')
            }
            libelleFermeture={t('composeur.modeles.fermer')}
        >
            {aConfirmer ? (
                <EtapeVerificationModele
                    modele={aConfirmer.modele}
                    titreRef={refTitreEtape}
                    onEnvoyer={() => {
                        const { modele, reservationId } = aConfirmer;
                        setAConfirmer(null);
                        onChoisir(modele, reservationId);
                    }}
                    onChoisirAutre={() => setAConfirmer(null)}
                />
            ) : enAttenteDeResa ? (
                <EtapeChoixReservation
                    modele={enAttenteDeResa}
                    reservations={reservations}
                    titreRef={refTitreEtape}
                    onChoisir={(reservationId) => {
                        setAConfirmer({ modele: enAttenteDeResa, reservationId });
                        setEnAttenteDeResa(null);
                    }}
                />
            ) : (
                <>
                    <Text variante="sousTitre" as="h2" className="mb-1">
                        {t('composeur.modeles.titre')}
                    </Text>
                    {locale && (
                        <Text variante="petit" ton="attenue" className="mb-3">
                            {t('composeur.modeles.langueClient', { langue: languageName(locale, langueMobile()) })}
                        </Text>
                    )}

                    {chargement && proposables.length === 0 && (
                        <Text variante="petit" ton="attenue">
                            {t('commun.chargement')}
                        </Text>
                    )}

                    {erreur && proposables.length === 0 && (
                        <EtatErreur texte={t('composeur.modeles.erreur')} onReessayer={onReessayer} />
                    )}

                    <ul className="flex flex-col gap-1">
                        {proposables.map((modele) => (
                            <li key={modele.id}>
                                {/* mobile-check-ignore — ligne de liste à deux niveaux :
                                    le Button du socle ne rend qu'un libellé sur une ligne. */}
                                <button
                                    type="button"
                                    onClick={() => choisir(modele)}
                                    className="flex min-h-touch w-full flex-col justify-center gap-0.5 rounded-xl px-3 py-2 text-left active:bg-main-color-light"
                                >
                                    <Text as="span" variante="petit" className="truncate font-semibold">
                                        {nomLisible(modele)}
                                    </Text>
                                    <Text as="span" variante="petit" ton="attenue" className="line-clamp-2">
                                        {apercu(modele)}
                                    </Text>
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </BottomSheet>
    );
}
